import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Building2, Globe, LogOut, Mail, MessageSquare, Phone, ShoppingBag } from "lucide-react";
import { API } from "../constants";
import { useLang } from "../i18n";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminPage() {
  const { t, lang } = useLang();
  const location = useLocation();
  const processedRef = useRef(false);
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isCallback = location.hash?.includes("session_id=");

  const loadRequests = async () => {
    try {
      const res = await fetch(`${API}/admin/quote-requests`, { credentials: "include" });
      if (res.status === 401) {
        setUser(null);
        return;
      }
      if (res.ok) setRequests(await res.json());
    } catch {}
  };

  useEffect(() => {
    if (isCallback) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (res.ok) {
          setUser(await res.json());
          await loadRequests();
        }
      } catch {}
      setChecking(false);
    })();
  }, [isCallback]);

  useEffect(() => {
    if (!isCallback || processedRef.current) return;
    processedRef.current = true;
    (async () => {
      const sid = location.hash.split("session_id=")[1]?.split("&")[0];
      try {
        const res = await fetch(`${API}/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sid }),
        });
        if (res.ok) {
          const u = await res.json();
          if (u.role === "admin") {
            setUser(u);
            await loadRequests();
          } else {
            setError(t("admin.err_login"));
          }
        }
      } catch {}
      window.history.replaceState({}, "", window.location.pathname);
    })();
  }, [isCallback]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(body.detail));
      setUser(body);
      await loadRequests();
    } catch (err) {
      setError(t("admin.err_login"));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setRequests([]);
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-obsidian" data-testid="admin-checking" />;
  }

  if (isCallback) {
    return <div className="min-h-screen bg-obsidian" data-testid="admin-oauth-callback" />;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-6" data-testid="admin-login-view">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md border border-gold/25 bg-cellar p-10">
          <h1 className="font-display text-2xl tracking-[0.2em] text-gold">{t("admin.login_title")}</h1>
          <p className="mt-3 text-sm text-parchment/60">{t("admin.login_sub")}</p>
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <a
            data-testid="admin-google-button"
            href={`https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/admin")}`}
            className="btn-outline-gold mt-8 w-full"
          >
            {t("admin.google_btn")}
          </a>
          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-gold/20" />
            <span className="text-[0.6rem] uppercase tracking-[0.3em] text-stone">{t("admin.or")}</span>
            <span className="h-px flex-1 bg-gold/20" />
          </div>
          <input
            data-testid="admin-email"
            type="email"
            className="io-input"
            placeholder={t("admin.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            data-testid="admin-password"
            type="password"
            className="io-input mt-5"
            placeholder={t("admin.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <p className="mt-4 text-xs text-crimson" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button type="submit" disabled={busy} data-testid="admin-login-button" className="btn-gold mt-8 w-full disabled:opacity-50">
            {busy ? t("admin.logging_in") : t("admin.login_btn")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian px-6 py-14 md:px-12" data-testid="admin-view">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl tracking-[0.2em] text-gold">{t("admin.title")}</h1>
            <p className="mt-2 text-sm text-parchment/60">{t("admin.sub")}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-outline-gold !px-5 !py-2.5 !text-[0.65rem]">
            <LogOut size={14} /> {t("admin.logout")}
          </button>
        </div>

        {requests.length === 0 ? (
          <p className="mt-16 text-sm text-parchment/50" data-testid="admin-empty">
            {t("admin.empty")}
          </p>
        ) : (
          <div className="mt-12 space-y-6">
            {requests.map((r, i) => (
              <article key={i} data-testid={`admin-request-card-${i}`} className="border border-gold/20 bg-cellar p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-lg text-parchment">{r.name}</h2>
                  <span className="text-[0.6rem] uppercase tracking-[0.25em] text-stone">
                    {r.created_at ? new Date(r.created_at).toLocaleString(lang) : ""}
                  </span>
                </div>
                <span className="mt-3 inline-block border border-gold/40 px-3 py-1 text-[0.6rem] uppercase tracking-[0.2em] text-gold">
                  {t(`form.${r.customer_type}`)}
                </span>
                <div className="mt-5 grid gap-2 text-sm text-parchment/75 md:grid-cols-2">
                  <a href={`mailto:${r.email}`} className="flex items-center gap-2 hover:text-gold">
                    <Mail size={14} className="text-gold" /> {r.email}
                  </a>
                  {r.phone && (
                    <span className="flex items-center gap-2">
                      <Phone size={14} className="text-gold" /> {r.phone}
                    </span>
                  )}
                  {r.company && (
                    <span className="flex items-center gap-2">
                      <Building2 size={14} className="text-gold" /> {r.company}
                    </span>
                  )}
                  {r.country && (
                    <span className="flex items-center gap-2">
                      <Globe size={14} className="text-gold" /> {r.country}
                    </span>
                  )}
                </div>
                <p className="mt-4 flex items-center gap-2 text-sm text-parchment/85">
                  <ShoppingBag size={14} className="shrink-0 text-gold" />
                  {r.items.map((it) => `${it.name} × ${it.qty}`).join(" · ")}
                </p>
                {r.message && (
                  <p className="mt-3 flex items-start gap-2 border-t border-gold/10 pt-3 text-sm italic text-parchment/60">
                    <MessageSquare size={14} className="mt-1 shrink-0 text-gold" /> {r.message}
                  </p>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
